/**
 * Shared Pro leave / back policy used by:
 * - Android hardware back (`useProHardwareBack`)
 * - Toolbar back in `ProTabLayout`
 * - Tab / drawer navigation that would leave the current screen
 * - Dirty-form guards (e.g. profile edit)
 *
 * `requestProLeave` order (first match wins):
 * 1. Dismiss the topmost `.pro-sheet` (ProPageModal / ProConfirmSheet)
 * 2. Run registered leave handlers (highest priority first); a handler that
 *    returns true consumes the attempt (e.g. shows an unsaved-changes sheet)
 * 3. Call the caller-supplied `proceed` (history.back, navigate(-1), …)
 *
 * `requestProHardwareBack` inserts one extra step after sheets:
 * close the Pro drawer if open (hardware back only — drawer item taps must
 * still navigate).
 *
 * Sheets are dismissed before leave handlers so hardware back on an open
 * confirm sheet cancels the dialog instead of double-prompting.
 */

export type ProLeaveHandler = (proceed: () => void) => boolean;

type RegisteredHandler = {
  id: number;
  priority: number;
  tryHandle: ProLeaveHandler;
};

let nextId = 1;
const handlers: RegisteredHandler[] = [];

/** Optional drawer closer — returns true when it consumed the event. */
let drawerCloser: (() => boolean) | null = null;

/**
 * Register a leave interceptor. Higher `priority` runs first.
 * Returns an unregister function.
 */
export function registerProLeaveHandler(
  tryHandle: ProLeaveHandler,
  priority = 0,
): () => void {
  const entry: RegisteredHandler = { id: nextId++, priority, tryHandle };
  handlers.push(entry);
  return () => {
    const idx = handlers.findIndex((h) => h.id === entry.id);
    if (idx >= 0) handlers.splice(idx, 1);
  };
}

/**
 * Install (or clear) the Pro drawer closer used only by hardware back.
 * `closer` should return true when the drawer was open and is now closing.
 */
export function setProDrawerCloser(closer: (() => boolean) | null): void {
  drawerCloser = closer;
}

/** @internal test helper */
export function __resetProLeaveHandlersForTests(): void {
  handlers.length = 0;
  nextId = 1;
  drawerCloser = null;
}

/** @internal test helper */
export function __proLeaveHandlerCountForTests(): number {
  return handlers.length;
}

/**
 * Dismiss the topmost open Pro sheet (last `.pro-sheet` in document order).
 * Prefer the close control so `onDismiss` runs through the modal's normal path.
 */
export function dismissTopProSheet(
  root: ParentNode = typeof document !== 'undefined' ? document : (null as unknown as ParentNode),
): boolean {
  if (!root || typeof root.querySelectorAll !== 'function') return false;
  const sheets = root.querySelectorAll('.pro-sheet');
  if (sheets.length === 0) return false;
  const top = sheets[sheets.length - 1] as HTMLElement;
  const closeBtn = top.querySelector<HTMLElement>(
    '.pro-sheet__close, [aria-label="Close"]',
  );
  if (closeBtn) {
    closeBtn.click();
    return true;
  }
  const backdrop = top.querySelector<HTMLElement>('.pro-sheet__backdrop');
  if (backdrop) {
    backdrop.click();
    return true;
  }
  return false;
}

function runLeaveHandlers(proceed: () => void): boolean {
  const ordered = [...handlers].sort((a, b) => b.priority - a.priority);
  for (const h of ordered) {
    if (h.tryHandle(proceed)) return true;
  }
  return false;
}

/**
 * Attempt to leave / go back under the Pro policy (toolbar, tabs, dirty guard).
 * Does not close the drawer — use `requestProHardwareBack` for that.
 */
export function requestProLeave(proceed: () => void): void {
  if (dismissTopProSheet()) return;
  if (runLeaveHandlers(proceed)) return;
  proceed();
}

/**
 * Hardware back entry point: sheets → drawer → leave handlers → proceed.
 */
export function requestProHardwareBack(proceed: () => void): void {
  if (dismissTopProSheet()) return;
  if (drawerCloser?.()) return;
  if (runLeaveHandlers(proceed)) return;
  proceed();
}
